import { motion, MotionValue } from "framer-motion";
import { memo, useCallback, useState } from "react";

const BrushCanvas = memo(function BrushCanvas({
  children,
  mode,
  cursorPositionMotionValue,
  onCursorMove,
  onScroll,
  onJumpToLive,
  pausedTimeMotionValue,
}: {
  children: React.ReactNode;
  mode: "live" | "archive";
  cursorPositionMotionValue: MotionValue<number>;

  onCursorMove: (clientX: number, rect: DOMRect) => void;
  onScroll?: (deltaX: number) => void;
  onJumpToLive?: () => void;
  pausedTimeMotionValue?: MotionValue<number>;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingCursor, setIsDraggingCursor] = useState(false);
  const [isHoveringCursor, setIsHoveringCursor] = useState(false);
  const [lastMouseX, setLastMouseX] = useState(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;

      // Check if click is near the cursor (within 15px for easier grabbing)
      const cursorPixelPosition = cursorPositionMotionValue.get() * rect.width;
      const distanceFromCursor = Math.abs(relativeX - cursorPixelPosition);

      if (distanceFromCursor <= 15) {
        // Clicking on cursor - start cursor dragging
        setIsDraggingCursor(true);
        e.preventDefault();
      } else {
        // Clicking elsewhere - start timeline scrolling (works in both live and archive mode)
        setIsDragging(true);
        setLastMouseX(e.clientX);
        e.preventDefault();
      }
    },
    [cursorPositionMotionValue, onCursorMove],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const cursorPixelPosition = cursorPositionMotionValue.get() * rect.width;
      const distanceFromCursor = Math.abs(relativeX - cursorPixelPosition);

      // Update hover state for cursor area
      setIsHoveringCursor(distanceFromCursor <= 15);

      if (isDraggingCursor) {
        // Dragging cursor
        onCursorMove(e.clientX, rect);
      } else if (isDragging && onScroll) {
        // Dragging timeline (works in both live and archive mode)
        const deltaX = e.clientX - lastMouseX;
        onScroll(deltaX);
        setLastMouseX(e.clientX);
      }
    },
    [
      isDraggingCursor,
      isDragging,
      mode,
      onScroll,
      lastMouseX,
      onCursorMove,
      cursorPositionMotionValue,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsDraggingCursor(false);
    setIsHoveringCursor(false);
  }, []);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (onScroll) {
        e.preventDefault();
        onScroll(-e.deltaX);
      }
    },
    [onScroll],
  );

  return (
    <motion.div
      style={{
        width: "100%",
        height: 60,
        position: "relative",
        cursor:
          isDraggingCursor || isDragging
            ? "grabbing"
            : isHoveringCursor
              ? "grab"
              : "grab",
        backgroundColor: "var(--background)",
        userSelect: "none",
        overflow: "hidden",
      }}
      layout
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        handleMouseUp();
        setIsHoveringCursor(false);
      }}
      onWheel={handleWheel}
    >
      {/* Jump to Live Button - only show in live mode when scrolled */}
      {mode === "live" &&
        pausedTimeMotionValue &&
        pausedTimeMotionValue.get() > 0 &&
        onJumpToLive && (
          <motion.button
            style={{
              position: "absolute",
              top: 8,
              right: 120, // Position to the left of the pause/play button
              zIndex: 10,
              background: "var(--primary)",
              border: "1px solid var(--primary)",
              borderRadius: 4,
              padding: "4px 8px",
              fontSize: "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              color: "white",
            }}
            onClick={onJumpToLive}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <span>🔴</span>
            <span style={{ fontSize: "10px" }}>Jump to Live</span>
          </motion.button>
        )}
      {children}
    </motion.div>
  );
});

export { BrushCanvas };
